import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { AuthLayout, Button, Field, Input, PasswordInput } from '@lp/portal-ui';
import { staffLoginSchema, type StaffSelf } from '@lp/shared';
import { api } from '../api.ts';
import { errorCode, errorFields, errorMessage } from '../lib/errors.ts';
import { type FieldErrors, validate } from '../lib/validate.ts';
import { Notice } from '../components/ui.tsx';

/** Only same-site paths are allowed as the post-login destination. */
function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const body = { email, password, ...(step === 'otp' ? { otp } : {}) };
    const v = validate(staffLoginSchema, body, { otp: '請輸入 6 位數字驗證碼', password: '請輸入密碼' });
    if (!v.ok) {
      setErrors(v.errors);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await api.post<{ user: StaffSelf }>('/auth/login', v.data);
      navigate(safeNext(params.get('next')), { replace: true });
    } catch (err) {
      const code = errorCode(err);
      if (code === 'OTP_REQUIRED') {
        setStep('otp');
        setOtp('');
      } else if (code === 'VALIDATION_FAILED') {
        setErrors(errorFields(err));
        setFormError(errorMessage(err));
      } else {
        setFormError(
          errorMessage(err, {
            CODE_INVALID: '驗證碼不正確，請再試一次。',
            CODE_EXPIRED: '驗證碼已過期，請輸入驗證程式上最新的驗證碼。',
            FORBIDDEN: '此帳戶不可登入品牌後台。',
          }),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout subtitle="品牌後台" title={step === 'password' ? '登入' : '雙重認證'}>
      <form onSubmit={submit} className="space-y-5" noValidate>
        {formError && <Notice tone="danger">{formError}</Notice>}
        {step === 'password' ? (
          <>
            <Field label="電郵" error={errors.email}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  invalid={!!errors.email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label="密碼" error={errors.password}>
              {(id) => (
                <PasswordInput
                  id={id}
                  autoComplete="current-password"
                  value={password}
                  invalid={!!errors.password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" loading={busy} className="w-full">
              登入
            </Button>
            <p className="text-center text-xs text-muted">忘記密碼？請聯絡你品牌的負責人或 LoyaltyPlus 支援。</p>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-xl bg-mint px-4 py-3 text-sm text-ink">
              <ShieldCheck className="mt-0.5 size-5 shrink-0 text-brand-700" />
              <span>
                此帳戶已開啟雙重認證。請打開驗證程式，輸入 <b>{email}</b> 的 6 位數字驗證碼。
              </span>
            </div>
            <Field label="驗證碼" error={errors.otp}>
              {(id) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  invalid={!!errors.otp}
                  className="text-center font-mono text-2xl tracking-[0.5em]"
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              )}
            </Field>
            <Button type="submit" loading={busy} className="w-full" disabled={otp.length !== 6}>
              驗證並登入
            </Button>
            <button
              type="button"
              className="mx-auto flex items-center gap-1.5 text-sm text-muted hover:text-ink"
              onClick={() => {
                setStep('password');
                setFormError(null);
                setErrors({});
              }}
            >
              <ArrowLeft className="size-4" />
              返回重新輸入電郵及密碼
            </button>
          </>
        )}
      </form>
    </AuthLayout>
  );
}
